import React from "react";
import { ExternalLink, ChevronRight, LucideIcon } from "lucide-react";

interface WebDashboardRibbonProps {
    title: string;
    description?: string;
    onClick?: () => void;
    icon?: LucideIcon;
    variant?: "info" | "warning" | "success" | "error";
}

const WebDashboardRibbon: React.FC<WebDashboardRibbonProps> = ({
    title,
    description,
    onClick,
    icon: Icon = ExternalLink,
    variant = "info",
}) => {
    const variantStyles = {
        info: {
            bg: "bg-gradient-to-r from-blue-50 to-indigo-50",
            border: "border-blue-500",
            iconBg: "bg-blue-100 group-hover:bg-blue-200",
            iconColor: "text-blue-600",
            textColor: "text-gray-900 group-hover:text-blue-700",
            chevronColor: "text-blue-600 group-hover:text-blue-700",
        },
        warning: {
            bg: "bg-gradient-to-r from-amber-50 to-yellow-50",
            border: "border-amber-500",
            iconBg: "bg-amber-100 group-hover:bg-amber-200",
            iconColor: "text-amber-600",
            textColor: "text-gray-900 group-hover:text-amber-700",
            chevronColor: "text-amber-600 group-hover:text-amber-700",
        },
        success: {
            bg: "bg-gradient-to-r from-green-50 to-emerald-50",
            border: "border-green-500",
            iconBg: "bg-green-100 group-hover:bg-green-200",
            iconColor: "text-green-600",
            textColor: "text-gray-900 group-hover:text-green-700",
            chevronColor: "text-green-600 group-hover:text-green-700",
        },
        error: {
            bg: "bg-gradient-to-r from-red-50 to-rose-50",
            border: "border-red-500",
            iconBg: "bg-red-100 group-hover:bg-red-200",
            iconColor: "text-red-600",
            textColor: "text-gray-900 group-hover:text-red-700",
            chevronColor: "text-red-600 group-hover:text-red-700",
        },
    };

    const styles = variantStyles[variant];

    return (
        <div className="mt-6">
            <div
                onClick={onClick}
                className={`${styles.bg} border-l-4 ${styles.border} rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200 ${
                    onClick ? "cursor-pointer group" : ""
                }`}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div
                            className={`flex-shrink-0 w-10 h-10 ${styles.iconBg} rounded-full flex items-center justify-center transition-colors`}
                        >
                            <Icon className={`w-5 h-5 ${styles.iconColor}`} />
                        </div>
                        <div>
                            <p className={`text-sm font-semibold ${styles.textColor} transition-colors`}>
                                {title}
                            </p>
                            {description && (
                                <p className="text-xs text-gray-600 mt-0.5">{description}</p>
                            )}
                        </div>
                    </div>
                    {onClick && (
                        <div className={`flex-shrink-0 ${styles.chevronColor} transition-colors`}>
                            <ChevronRight className="w-5 h-5" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WebDashboardRibbon;

